import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Trash2, Plus, Eye, EyeOff, ExternalLink, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { key: 'partner', label: 'Partenaire' },
    { key: 'client', label: 'Client' },
    { key: 'ministry', label: 'Ministère' },
    { key: 'donor', label: 'Bailleur' },
    { key: 'ngo', label: 'ONG' },
];

const emptyForm = { name: '', logo_url: '', link_url: '', category: 'partner', position: 0, is_published: true };

export default function TrustedLogosAdmin({ auth }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [settings, setSettings] = useState({
        trusted_logos_enabled: true,
        trusted_logos_title_fr: '',
        trusted_logos_title_en: '',
        trusted_logos_subtitle_fr: '',
        trusted_logos_subtitle_en: '',
    });
    const [savingSettings, setSavingSettings] = useState(false);
    const [editing, setEditing] = useState(null); // null | { ...form } | { id, ... }
    const [busy, setBusy] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/super-admin/trusted-logos`, auth);
            setItems(r.data.items || []);
            if (r.data.settings && Object.keys(r.data.settings).length) {
                setSettings({
                    trusted_logos_enabled: r.data.settings.trusted_logos_enabled ?? true,
                    trusted_logos_title_fr: r.data.settings.trusted_logos_title_fr || '',
                    trusted_logos_title_en: r.data.settings.trusted_logos_title_en || '',
                    trusted_logos_subtitle_fr: r.data.settings.trusted_logos_subtitle_fr || '',
                    trusted_logos_subtitle_en: r.data.settings.trusted_logos_subtitle_en || '',
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué (schéma v21 appliqué ?)');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const saveSettings = async (partial) => {
        setSavingSettings(true);
        try {
            const r = await axios.patch(`${API}/super-admin/platform-settings`, partial, auth);
            const s = r.data.settings || {};
            setSettings((prev) => ({ ...prev, ...partial, ...s }));
            toast.success('Réglages enregistrés');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec de l’enregistrement');
        } finally {
            setSavingSettings(false);
        }
    };

    const submitForm = async () => {
        if (!editing) return;
        if (!editing.name?.trim() || !editing.logo_url?.trim()) {
            toast.error('Nom et URL du logo sont requis');
            return;
        }
        setBusy(true);
        try {
            const body = {
                name: editing.name.trim(),
                logo_url: editing.logo_url.trim(),
                link_url: editing.link_url?.trim() || null,
                category: editing.category,
                position: Number(editing.position) || 0,
                is_published: !!editing.is_published,
            };
            if (editing.id) {
                await axios.patch(`${API}/super-admin/trusted-logos/${editing.id}`, body, auth);
                toast.success('Logo mis à jour');
            } else {
                await axios.post(`${API}/super-admin/trusted-logos`, body, auth);
                toast.success('Logo ajouté');
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec de la sauvegarde');
        } finally {
            setBusy(false);
        }
    };

    const togglePublished = async (item) => {
        try {
            await axios.patch(`${API}/super-admin/trusted-logos/${item.id}`, {
                name: item.name,
                logo_url: item.logo_url,
                link_url: item.link_url || null,
                category: item.category,
                position: item.position || 0,
                is_published: !item.is_published,
            }, auth);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec');
        }
    };

    const doDelete = async () => {
        if (!confirmDel) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/super-admin/trusted-logos/${confirmDel.id}`, auth);
            toast.success('Logo supprimé');
            setConfirmDel(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Suppression échouée');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="sa-trusted-logos">
            {/* Settings card */}
            <div className="card-soft p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h3 className="font-heading text-xl">Carrousel « Ils nous font confiance »</h3>
                        <p className="text-sm text-foreground/65 mt-1">
                            Activez ou désactivez le bandeau sous le Hero de la page d’accueil et personnalisez son titre.
                        </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <Label htmlFor="trusted-enabled" className="text-sm font-medium">
                            {settings.trusted_logos_enabled ? 'Activé' : 'Désactivé'}
                        </Label>
                        <Switch
                            id="trusted-enabled"
                            checked={!!settings.trusted_logos_enabled}
                            disabled={savingSettings}
                            onCheckedChange={(v) => saveSettings({ trusted_logos_enabled: !!v })}
                            data-testid="trusted-logos-toggle"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-foreground/60">Titre (FR)</Label>
                        <Input
                            value={settings.trusted_logos_title_fr}
                            onChange={(e) => setSettings({ ...settings, trusted_logos_title_fr: e.target.value })}
                            onBlur={(e) => saveSettings({ trusted_logos_title_fr: e.target.value })}
                            placeholder="Ils nous font confiance"
                            data-testid="trusted-logos-title-fr"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-foreground/60">Title (EN)</Label>
                        <Input
                            value={settings.trusted_logos_title_en}
                            onChange={(e) => setSettings({ ...settings, trusted_logos_title_en: e.target.value })}
                            onBlur={(e) => saveSettings({ trusted_logos_title_en: e.target.value })}
                            placeholder="They trust us"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-foreground/60">Sous-titre (FR)</Label>
                        <Textarea
                            value={settings.trusted_logos_subtitle_fr}
                            onChange={(e) => setSettings({ ...settings, trusted_logos_subtitle_fr: e.target.value })}
                            onBlur={(e) => saveSettings({ trusted_logos_subtitle_fr: e.target.value })}
                            rows={2}
                            placeholder="Organisations, ONG, ministères et bailleurs..."
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-foreground/60">Subtitle (EN)</Label>
                        <Textarea
                            value={settings.trusted_logos_subtitle_en}
                            onChange={(e) => setSettings({ ...settings, trusted_logos_subtitle_en: e.target.value })}
                            onBlur={(e) => saveSettings({ trusted_logos_subtitle_en: e.target.value })}
                            rows={2}
                            placeholder="Organizations, NGOs, ministries..."
                        />
                    </div>
                </div>
            </div>

            {/* Logos catalog */}
            <div className="card-soft p-5">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className="font-heading text-xl">Logos affichés</h3>
                        <p className="text-sm text-foreground/65 mt-1">
                            {items.length} logo{items.length > 1 ? 's' : ''} · tri par position croissante
                        </p>
                    </div>
                    <Button onClick={() => setEditing({ ...emptyForm, position: items.length })} data-testid="trusted-logos-add">
                        <Plus size={16} className="mr-2" /> Ajouter un logo
                    </Button>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-foreground/60"><Loader2 className="inline animate-spin mr-2" size={16} /> Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed rounded-lg text-foreground/55">
                        Aucun logo pour le moment. Cliquez sur « Ajouter un logo » pour commencer.
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((it) => (
                            <div
                                key={it.id}
                                className={`relative border rounded-xl p-4 bg-white flex flex-col gap-3 ${!it.is_published ? 'opacity-55' : ''}`}
                                data-testid={`trusted-logo-row-${it.id}`}
                            >
                                <div className="flex items-center justify-between text-xs text-foreground/55">
                                    <span className="inline-flex items-center gap-1.5">
                                        <GripVertical size={12} /> #{it.position || 0}
                                    </span>
                                    <span className="px-2 py-0.5 rounded bg-surface-muted">{CATEGORIES.find(c => c.key === it.category)?.label || it.category}</span>
                                </div>
                                <div className="h-20 flex items-center justify-center bg-surface-muted/50 rounded-md p-2">
                                    <img src={it.logo_url} alt={it.name} className="max-h-full max-w-full object-contain" />
                                </div>
                                <div>
                                    <div className="font-medium text-sm">{it.name}</div>
                                    {it.link_url && (
                                        <a href={it.link_url} target="_blank" rel="noopener noreferrer"
                                           className="text-xs text-primary inline-flex items-center gap-1 hover:underline mt-0.5">
                                            <ExternalLink size={11} /> Lien
                                        </a>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mt-auto">
                                    <Button variant="outline" size="sm" onClick={() => setEditing(it)} data-testid={`trusted-logo-edit-${it.id}`}>
                                        Modifier
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => togglePublished(it)} title={it.is_published ? 'Masquer' : 'Publier'}>
                                        {it.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDel(it)} className="text-destructive hover:text-destructive ml-auto">
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? 'Modifier le logo' : 'Ajouter un logo'}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <Label>Nom *</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                       placeholder="Ex : Banque africaine de développement" data-testid="trusted-logo-input-name" />
                            </div>
                            <div>
                                <Label>URL du logo *</Label>
                                <Input value={editing.logo_url} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })}
                                       placeholder="https://.../logo.png ou /brand/xxx.png" data-testid="trusted-logo-input-url" />
                                <p className="text-xs text-foreground/55 mt-1">PNG transparent ou SVG recommandé, hauteur 56–80px.</p>
                            </div>
                            <div>
                                <Label>Lien (optionnel)</Label>
                                <Input value={editing.link_url || ''} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })}
                                       placeholder="https://organisation.org" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Catégorie</Label>
                                    <select
                                        value={editing.category}
                                        onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                    >
                                        {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label>Position</Label>
                                    <Input type="number" value={editing.position}
                                           onChange={(e) => setEditing({ ...editing, position: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 pt-2">
                                <Switch
                                    id="logo-published"
                                    checked={!!editing.is_published}
                                    onCheckedChange={(v) => setEditing({ ...editing, is_published: !!v })}
                                />
                                <Label htmlFor="logo-published" className="cursor-pointer">Publié sur la landing</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
                        <Button onClick={submitForm} disabled={busy} data-testid="trusted-logo-save">
                            {busy && <Loader2 size={14} className="mr-2 animate-spin" />}<Save size={14} className="mr-2" /> Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm delete */}
            <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Supprimer le logo</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-foreground/70">
                        Confirmer la suppression de <strong>{confirmDel?.name}</strong> ? Cette action est irréversible.
                    </p>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmDel(null)} disabled={busy}>Annuler</Button>
                        <Button variant="destructive" onClick={doDelete} disabled={busy}>
                            {busy && <Loader2 size={14} className="mr-2 animate-spin" />}<Trash2 size={14} className="mr-2" /> Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
